import {CalendarIcon, X} from "lucide-react";
import {useMemo, useState} from "react";
import {PropsBase, PropsSingle} from "react-day-picker";

import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {useStoredCompany} from "@/src/modules/company/store";
import {formatDate, moment} from "@/src/modules/core/lib";

interface DatePickerProps {
  placeholder?: string;
  pickerDisabled?: boolean;
  className?: string;
  selected: Date | null;
  onSelect: (date: Date | null) => void;
  selectedLabel?: string | null;
}

export default function DatePicker({
  className = "",
  placeholder = "Select date",
  pickerDisabled = false,
  selected,
  onSelect,
  selectedLabel,
  ...props
}: DatePickerProps & PropsBase & Omit<PropsSingle, "selected" | "mode" | "onSelect">) {
  const company = useStoredCompany();
  const [isOpen, setIsOpen] = useState(false);
  const timezone = useMemo(() => {
    return company?.timezone;
  }, [company]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <div className="relative">
        <PopoverTrigger
          render={
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start border-slate-300 text-left font-normal",
                className,
                !selected && "text-slate-500",
              )}
              disabled={pickerDisabled}
              onClick={() => !pickerDisabled && setIsOpen(!isOpen)}
            />
          }
        >
          {selected
            ? (selectedLabel ?? formatDate(selected.toISOString().slice(0, 10)))
            : placeholder}
        </PopoverTrigger>
        <div className="absolute top-1/2 right-1 -translate-y-1/2">
          {selected ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onSelect(null);
                setIsOpen(false);
              }}
            >
              <X className="size-4 text-slate-500" />
              <span className="sr-only">Clear</span>
            </Button>
          ) : (
            <div className="px-3">
              <CalendarIcon className="size-4 text-slate-500" />
            </div>
          )}
        </div>
      </div>
      <PopoverContent className="w-auto p-0">
        <Calendar
          {...props}
          ISOWeek
          mode="single"
          selected={selected ?? undefined}
          timeZone={timezone}
          onSelect={(d) => {
            const date = d ? moment(`${d.toISOString().slice(0, 10)}T00:00:00`).toDate() : null;

            onSelect && onSelect(date);
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
